import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMundusStore } from '../stores/mundus.store';
import { useAuthStore } from '../stores/auth.store';
import { Calendar, Settings } from 'lucide-react';
import Chat from '../components/Chat';
import WhatsAppCTA from '../components/dashboard/WhatsAppCTA';
import WinsSummaryCard from '../components/dashboard/WinsSummaryCard';
import MundusTemplatePicker from '../components/dashboard/MundusTemplatePicker';
import OnboardingChecklist from '../components/dashboard/OnboardingChecklist';
import DailyChallenge from '../components/dashboard/DailyChallenge';
import BadgesCard from '../components/dashboard/BadgesCard';
import ReferralCard from '../components/dashboard/ReferralCard';
import AtentoTaskBoard from '../components/dashboard/AtentoTaskBoard';

export const TodayDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { munduses, fetchMunduses, isLoading } = useMundusStore();
  const { user } = useAuthStore();
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    fetchMunduses().finally(() => setHasFetched(true));
  }, []);

  const activeMundus = munduses.find((m) => m.isActive) || munduses[0];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || '';

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  // Loading state
  if (!hasFetched || isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  // STATE A: No Mundus — show template picker
  if (!activeMundus) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-blue-600">Mando</span>
                <span className="text-slate-300">|</span>
                <span className="text-sm text-slate-500">{getGreeting()}, {firstName}</span>
              </div>
              <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                <Calendar className="w-3 h-3" />
                {formatDate()}
              </p>
            </div>
            <button
              onClick={() => navigate('/settings')}
              className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          <MundusTemplatePicker />
          <WhatsAppCTA />
        </main>
      </div>
    );
  }

  // STATE B: Has Mundus — show chat + wins + WhatsApp
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-blue-600">Mando</span>
              <span className="text-slate-300">|</span>
              <span className="text-sm text-slate-500">{getGreeting()}, {firstName}</span>
            </div>
            <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
              <Calendar className="w-3 h-3" />
              {formatDate()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/mundus/${activeMundus.id}`)}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              Full workspace →
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {/* WhatsApp CTA (collapses when linked) */}
        <WhatsAppCTA />

        {/* Daily Challenge */}
        <DailyChallenge
          mundusId={activeMundus.id}
          onAction={(msg) => {
            // Navigate to chat input — the Chat handles sending
            const chatInput = document.querySelector('textarea');
            if (chatInput) {
              (chatInput as HTMLTextAreaElement).value = msg;
              chatInput.dispatchEvent(new Event('input', { bubbles: true }));
              chatInput.focus();
            }
          }}
        />

        {/* Chat + Sidebar */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-4">
          {/* Chat — main column */}
          <div className="lg:col-span-2 mb-4 lg:mb-0">
            <Chat
              mundusId={activeMundus.id}
              agents={activeMundus.agents || []}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <AtentoTaskBoard mundusId={activeMundus.id} />
            <OnboardingChecklist
              hasMundus={true}
              hasMessages={(activeMundus as any)?._count?.reports > 0 || true}
              mundusId={activeMundus.id}
            />
            <WinsSummaryCard mundusId={activeMundus.id} />
            <BadgesCard />
            <ReferralCard />

            {/* Create new world */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Want a different focus?</p>
              <button
                onClick={() => navigate('/munduses')}
                className="w-full px-3 py-2 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors font-medium"
              >
                + Create new world
              </button>
            </div>
          </div>
        </div>

        {/* Footer branding */}
        <div className="text-center py-4">
          <p className="text-xs text-slate-400">
            Powered by <span className="font-medium text-slate-500">Atento</span> + <span className="font-medium text-slate-500">Mando</span>
          </p>
        </div>
      </main>
    </div>
  );
};
